import { useState } from 'react';
import { useTracking } from 'react-tracking';

import { useMount } from '@simplywallst/ui-core';

import EarningReportOutline from '@simplywallst/ui-core/icons/EarningReportOutline';
import ValuationOutline from '@simplywallst/ui-core/icons/ValuationOutline';
import ArticleOutline from '@simplywallst/ui-core/icons/ArticleOutline';
import DividendOutline from '@simplywallst/ui-core/icons/DividendOutline';
import ExecutiveOutline from '@simplywallst/ui-core/icons/ExecutiveOutline';
import NewRiskOutline from '@simplywallst/ui-core/icons/NewRiskOutline';
import MarketInsightsOutline from '@simplywallst/ui-core/icons/MarketInsightsOutline';

import { Tab } from './Tab';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { LoadingState } from './LoadingState';
import { PopulatedState } from './PopulatedState';

import { useParams } from '@/hooks/location/useParams';
import { useUpdates } from './useUpdates';

import type { Section } from './useUpdates';

type Tab = {
  label: string;
  value: Section;
  icon: JSX.Element;
};

const sections: Tab[] = [
  {
    label: 'Earnings',
    value: 'earnings',
    icon: <EarningReportOutline />,
  },
  {
    label: 'Valuations',
    value: 'valuations',
    icon: <ValuationOutline />,
  },
  {
    label: 'Narrative',
    value: 'narrative',
    icon: <ArticleOutline />,
  },
  {
    label: 'Dividends',
    value: 'dividends',
    icon: <DividendOutline />,
  },
  {
    label: 'People',
    value: 'people',
    icon: <ExecutiveOutline />,
  },
  {
    label: 'Risk and Legal',
    value: 'riskAndLegal',
    icon: <NewRiskOutline />,
  },
  {
    label: 'Other',
    value: 'other',
    icon: <MarketInsightsOutline />,
  },
];

type Props = {
  demoPortfolioId?: string;
};

export const UpdatesPanel = ({ demoPortfolioId }: Props) => {
  const { trackEvent, Track } = useTracking({
    type: 'track',
    modifier: 'UpdatesPanel',
  });

  const [state, setState] = useState<Section>('earnings');

  const { portfolioId } = useParams<{ portfolioId: string }>();
  const { data, status } = useUpdates(demoPortfolioId ?? portfolioId);

  useMount(() => {
    trackEvent({
      action: 'landed',
    });
  });

  const { updates } = data?.Portfolio || {};

  return (
    <Track>
      <section className="md:px-x2 lg:px-0">
        <div className="@container/updates">
          <div className="grid gap-x1 @[768px]:grid-cols-[280px_1fr] @[768px]:gap-x3">
            <div className="sticky top-[calc(64px+env(safe-area-inset-top))] z-sticky bg-legacy-charcoal pt-x1 @[768px]:relative @[768px]:top-0 @[768px]:z-auto @[768px]:pt-x0">
              <ul className="px-x2 @[768px]:sticky @[768px]:top-[calc(80px+env(safe-area-inset-top))] @[768px]:z-sticky @[768px]:px-x0">
                {sections.map(({ label, value, icon }) => (
                  <li
                    key={`tab-${value}`}
                    className="mb-x0_5 mr-x0_5 inline-block @[768px]:m-x0 @[768px]:block"
                  >
                    <Tab
                      label={label}
                      value={value}
                      selected={state === value}
                      icon={icon}
                      updateCount={
                        (updates &&
                          updates[value]?.filter((n) => n.isRecent).length) ||
                        0
                      }
                      onClick={(e) => {
                        window && window.scrollTo({ top: 0 });
                        setState(e.currentTarget.value as Section); // TODO: fix type
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="grid bg-surface-1 p-x2 @[768px]:rounded-md @[768px]:p-x3"
              aria-labelledby="Earnings"
              role="tab-panel"
            >
              {status === 'pending' ? (
                <LoadingState />
              ) : status === 'error' ? (
                <ErrorState />
              ) : updates && updates[state] && updates[state].length > 0 ? (
                <PopulatedState updates={updates[state]} />
              ) : (
                <EmptyState />
              )}
            </div>
          </div>
        </div>
      </section>
    </Track>
  );
};
